import {
  contractsCreateMutation,
  contractsPartialUpdateMutation,
  contractsRetrieveOptions,
  CurrenciesListListData,
  currenciesListListInfiniteOptions,
  Dropdown,
  leadsContractsListQueryKey,
  leadsLogsListQueryKey,
  opportunitiesContractsListQueryKey,
  opportunitiesLogsListQueryKey,
  opportunitiesRetrieveOptions,
  PaginatedDropdownList,
  PaginatedReturnUserListReadable,
  ReturnUserReadable,
  UsersListData,
  usersListInfiniteOptions
} from '@crm/api'
import { InfiniteSelect } from '@crm/components'
import { useDefaultCurrency } from '@crm/hooks/useDefaultCurrency'
import { getSelectIdValue, userTransformResults } from '@crm/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Col, Form, Input, Row } from 'antd'
import { AxiosError } from 'axios'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

type Props = {
  id?: number
  formName: string
  closeModal?: () => void
  opportunityIdProp?: number
  onSuccess?: () => void
}

export const ContractForm = ({
  id,
  formName,
  closeModal,
  opportunityIdProp,
  onSuccess
}: Props) => {
  const { t } = useLocales()
  const { id: leadId, opportunityId } = useParams()
  const [form] = Form.useForm()
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { notification } = App.useApp()
  const { defaultCurrency } = useDefaultCurrency()

  const effectiveOpportunityId = opportunityIdProp
    ? String(opportunityIdProp)
    : opportunityId

  const { data } = useQuery({
    ...contractsRetrieveOptions({ path: { id: Number(id) } }),
    enabled: !!id
  })

  const { data: opportunity } = useQuery({
    ...opportunitiesRetrieveOptions({
      path: { id: Number(effectiveOpportunityId) }
    }),
    enabled: !!effectiveOpportunityId
  })

  useEffect(() => {
    if (data) {
      const { currency, owner, ...formData } = data
      form.setFieldsValue({
        ...formData,
        currency: currency?.name ?? defaultCurrency,
        owner: owner?.name
      })
    } else if (effectiveOpportunityId) {
      form.setFieldsValue({
        currency: opportunity?.currency?.id ?? defaultCurrency,
        owner: opportunity?.owner?.id
      })
    } else {
      form.setFieldsValue({ currency: defaultCurrency })
    }
  }, [data, form, effectiveOpportunityId, opportunity, defaultCurrency])

  const onMutation = {
    onError: (error: AxiosError) => showApiErrors(error, form),
    onSuccess: () => {
      const queriesToInvalidate = [
        ...(leadId
          ? [
              leadsContractsListQueryKey({ path: { lead_id: String(leadId) } }),
              leadsLogsListQueryKey({ path: { lead_id: String(leadId) } })
            ]
          : []),
        ...(effectiveOpportunityId
          ? [
              opportunitiesContractsListQueryKey({
                path: { opportunity_id: String(effectiveOpportunityId) }
              }),
              opportunitiesLogsListQueryKey({
                path: { opportunity_id: String(effectiveOpportunityId) }
              })
            ]
          : [])
      ]

      invalidateQueries(queriesToInvalidate)
      closeModal?.()
      onSuccess?.()
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
    }
  }

  const { mutate: createMutate } = useMutation({
    ...contractsCreateMutation(),
    ...onMutation
  })
  const { mutate: editMutate } = useMutation({
    ...contractsPartialUpdateMutation(),
    ...onMutation
  })

  const onSubmit = () => {
    const { owner, currency, ...rest } = form.getFieldsValue()

    const requestBody = {
      ...rest,
      currency: getSelectIdValue(currency, data?.currency?.id),
      opportunity: data?.opportunity?.id ?? Number(effectiveOpportunityId),
      product: data?.product?.id ?? opportunity?.product?.id,
      owner: getSelectIdValue(owner, data?.owner?.id),
      lead: opportunityIdProp ? opportunity?.lead?.id : leadId
    }

    if (id) {
      editMutate({ path: { id: Number(id) }, body: requestBody })
    } else {
      createMutate({ body: requestBody })
    }
  }

  return (
    <Form name={formName} form={form} onFinish={onSubmit} layout="vertical">
      <Row>
        <Col span={24}>
          <Form.Item
            name="number"
            label={t('contracts.contract_number')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col span={18}>
          <Form.Item
            name="value"
            label={t('contracts.value')}
            rules={[{ required: true }]}
          >
            <Input type="number" />
          </Form.Item>
        </Col>

        <Col span={6}>
          <Form.Item
            name="currency"
            label={t('contracts.currency')}
            rules={[{ required: true }]}
          >
            <InfiniteSelect<
              CurrenciesListListData,
              PaginatedDropdownList,
              Dropdown
            >
              popupMatchSelectWidth
              placeholder={t('main.select')}
              getQueryFunction={currenciesListListInfiniteOptions}
              baseOptions={{
                query: {
                  ordering: '-is_default',
                  preference: opportunity?.currency?.id
                    ? [opportunity.currency.id]
                    : undefined
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Form.Item name="owner" label={t('contracts.owner')}>
            <InfiniteSelect<
              UsersListData,
              PaginatedReturnUserListReadable,
              ReturnUserReadable
            >
              popupMatchSelectWidth
              placeholder={t('main.select')}
              getQueryFunction={usersListInfiniteOptions}
              getOptions={({ options }) => userTransformResults(options)}
              baseOptions={{
                query: {
                  preference: opportunity?.owner?.id
                    ? [opportunity.owner.id]
                    : undefined
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  )
}
